<template>
  <div>
    <div
      :class="[
        'card-container cursor-pointer',
        { 'is-selected': panel[selectProp] },
        { 'not-allowed-bg not-allowed-font-color' : !isAllowed },
      ]"
      @click="clickCustomPanel"
    >
      <div class="card-head">
        <div class="code-section">
          <img
            :src="prefix[prefixStyle]"
            alt="checkbox"
            :class="['checkbox', { 'not-allowed-checkbox' : !isAllowed }]"
          >
          <p class="panel-code">{{ id }}</p>
        </div>
        <p v-if="displayPrice" class="price">{{ displayPrice }}</p>
      </div>

      <div class="card-body">
        <p :class="['card-title', { 'not-allowed-font-color': !isAllowed }]">
          {{ name }}
        </p>

        <div v-if="sampleTypeTagInfo.keys.length > 0" class="tag-list">
          <TestSampleTypeTagNew
            v-for="tag in sampleTypeTagInfo.keys"
            :key="tag"
            :type="tag"
          />
        </div>
      </div>
    </div>
    <ItemNotAllowedDialog
      v-if="isShowCustomPanelNotAllowedDialog"
      :is-show-dialog="isShowCustomPanelNotAllowedDialog"
      :item-category="panel.category"
      :item-name="name"
      :dialog-type="notAllowedDialogType"
      @close="handleDialogCloseBtn"
      @done="handleDialogDoneBtn"
    />
  </div>
</template>

<script>
import TestSampleTypeTagNew from "../../components/TestSampleTypeTagNew.vue";
import ItemNotAllowedDialog from "../ItemNotAllowedDialog.vue";
import { mapGetters, mapState } from "vuex";
import { isItemAllowedForCurrentMenu, isItemAllowedForPatientAge, isCustomPanelContainOfflineItem, isCustomPanelContainMissingItem } from "./customPanelCard";
import { getSampleTypeSetsNew, getSampleTypeTagInfoFromSetsNew } from "@/utils/va-order-test/sampleTypes.js";

export default {
  components: {
    TestSampleTypeTagNew,
    ItemNotAllowedDialog
  },
  props: {
    panel: {
      type: Object,
      default: null,
    },
    id: {
      type: Number,
      default: 0
    },
    name: {
      type: String,
      default: ""
    },
    selectProp: {
      type: String,
      default: "isSelect"
    },
    isFavPanelOpen: {
      type: Boolean,
      default: false
    }
  },
  emits:['click'],
  data() {
    return {
      isShowCustomPanelNotAllowedDialog: false,
      prefix: {}
    }
  },
  computed: {
    ...mapState("orderTest",["isDbsVersion", "addOnFixedCustomPanel", "addOnRequiredMoreNoOfTubesTests", "addOnFixedVaVwTest", "addOnFixedVaVwTestWithParents","isAddOnOrder", "addOnCustomRequiredMoreNoOfTubesTests"]),
    ...mapState("orderTest/tests", ["isVaPanelHidden", "vaBfsList"]),
    ...mapGetters("orderTest", ["orderTestSupportObj"]),
    ...mapGetters("orderTest/tests", ["nonOrderableTestIds", "testList"]),
    ...mapGetters("orderTest/panels", ["nonOrderableDiscountPanelIds"]),
    ...mapGetters("orderTest/order", ["patientAge"]),

    prefixStyle() {
      return this.panel[this.selectProp] ? "allCheck" : "unCheck";
    },

    panelContainsVa() {
      if (!this.isVaPanelHidden) return false;
      const vaTestIds = new Set(this.vaBfsList.map(n => n.id));
      return this.panel.bundleItemList?.some(item => vaTestIds.has(item.itemId)) ?? false;
    },

    isAllowed() {
      if (this.panelContainsVa) return false;
      return this.isItemAllowedForCurrentMenu && this.isAllowedForPatientAge && !this.isCustomPanelContainOfflineItem && !this.isCustomPanelContainMissingItem;
    },

    isItemAllowedForCurrentMenu() {
      return isItemAllowedForCurrentMenu(this.isDbsVersion, this.panel, "DBS")
    },

    isAllowedForPatientAge() {
      return isItemAllowedForPatientAge(this.panel, this.orderTestSupportObj, this.patientAge);
    },

    isCustomPanelContainOfflineItem() {
      // custom panel with micronutrient and ctp tests still be orderable
      let { noMicronutrientAndNoCtp } = this.nonOrderableTestIds;
      return isCustomPanelContainOfflineItem(this.panel, noMicronutrientAndNoCtp, this.nonOrderableDiscountPanelIds)
    },

    isCustomPanelContainMissingItem() {
      return isCustomPanelContainMissingItem(this.panel, this.testList);
    },

    notAllowedDialogType() {
      if (this.panelContainsVa) return 'notAllowedForCurrentPatient';
      if (this.isCustomPanelContainOfflineItem || this.isCustomPanelContainMissingItem) return 'notAllowedForOfflineItem';
      if (!this.isAllowedForPatientAge) return 'notAllowedForPatientAge';
      if (!this.isItemAllowedForCurrentMenu) return 'notAllowedForMenu';
      return '';
    },

    sampleTypeTagInfo() {
      const sampleSets = getSampleTypeSetsNew(this.panel);
      return getSampleTypeTagInfoFromSetsNew(sampleSets);
    },

    containTestFromAddOnRequireMoreTube() {
      if(!this.isAddOnOrder) return false
      return !this.panel.bundleItemList ||
          this.panel.bundleItemList.length === 0 ||
          this.panel.bundleItemList.map(i => i.itemId)
          .filter(x =>
           [300,371,339].includes(x) ||
            x === undefined ||
            this.addOnRequiredMoreNoOfTubesTests.map(i => i.id).includes(x)
          ).length > 0
    },

    containTestFromAddOnPrevOrdered() {
      if(!this.isAddOnOrder) return false
      return !this.panel.bundleItemList ||
      this.panel.bundleItemList.length === 0 ||
      this.panel.bundleItemList.map(i => i.itemId)
          .filter(x =>
           [300,371,339].includes(x) ||
            x === undefined ||
            this.addOnFixedVaVwTest.has(x) ||
            this.addOnFixedVaVwTestWithParents.has(x)
          ).length > 0
    },
    addOnRequireMoreThanOneTube() {
      return this.addOnCustomRequiredMoreNoOfTubesTests.includes(this.id)
    },

    displayPrice() {
      if (!this.panel) return "";
      const price = this.panel.total ?? this.panel.price;
      if (price === undefined || price === null) return "";
      const symbol = this.$store.state.currency?.symbol || "$";
      const decimals = this.panel.category === "vw" ? 0 : 2;
      return `${symbol}${Number(price).toFixed(decimals)}`;
    },
  },
  mounted() {
    this.prefix = this.getPrefixPictureBySelectProp(this.selectProp);
  },
  methods: {
    clickCustomPanel() {
      if (!this.isAllowed) {
        this.isShowCustomPanelNotAllowedDialog = true;
        return;
      }
      this.$emit('click', this.panel);
    },
    handleDialogCloseBtn() {
      this.isShowCustomPanelNotAllowedDialog = false
    },
    handleDialogDoneBtn(isSameDBSPanel) {
      this.isShowCustomPanelNotAllowedDialog = false
      if(!isSameDBSPanel) {
        this.$emit('click', this.panel);
      }
    },
    getPrefixPictureBySelectProp(selectProp) {
      if (selectProp === "isSelectForFavorite") {
        return {
          unCheck: require("@/assets/orderTest/fav-icon-empty.svg"),
          partCheck: require("@/assets/orderTest/checkbox_indeterminate_box.svg"),
          allCheck: require("@/assets/orderTest/fav-icon-filled.svg")
        }
      } else {
        return {
          unCheck: require("@/assets/orderTest/check_box_outline_blank_24px.svg"),
          partCheck: require("@/assets/orderTest/checkbox_indeterminate_box.svg"),
          allCheck: require("@/assets/orderTest/Checkbox(all).svg")
        }
      }
    }
  }
};
</script>

<style scoped>
.card-container {
  min-height: 98px;
  background: #FFF;
  box-shadow: 0 4px 12px 0 rgba(26, 32, 44, 0.06);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid transparent;
}

.card-container:hover {
  background: #FFF;
}

.is-selected {
  border-color: #004879;
  background: #F9FBFD;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.code-section {
  display: flex;
  align-items: center;
  gap: 7px;
}

.panel-code {
  color: #000;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.2px;
}

.card-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox {
  height: 22px;
  width: 22px;
  min-width: 22px;
  filter: brightness(0) saturate(100%) invert(8%) sepia(100%) saturate(3500%) hue-rotate(190deg);
}

.not-allowed-checkbox {
  opacity: 0.4;
}

.card-title {
  letter-spacing: 0.1px;
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  /* EHR_TitleS_14_WM */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  letter-spacing: 0.1px;
  margin: 0;
}

.not-allowed-bg {
  background: #F2F2F2;
}

.not-allowed-font-color {
  color: #979797;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.price {
  color: #20A7C1;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  white-space: nowrap;
  margin: 0;
}
</style>
